// client/src/pages/admin/AdminDiscounts.js
// Admin management for CUSTOMER DISCOUNTS — with wholesaler filter dropdown
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../services/api';
import axios from 'axios';
import { Percent, ToggleLeft, ToggleRight, Trash2, Search } from 'lucide-react';

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AdminDiscounts = () => {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [wholesalers, setWholesalers] = useState([]);
  const [filters, setFilters] = useState({ active: '', wholesalerId: '', search: '' });
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/admin/login');
  }, [user, navigate]);

  // Fetch wholesaler list for dropdown
  const fetchWholesalers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/platform/wholesalers-list`, { headers: getHeaders() });
      setWholesalers(res.data.wholesalers || []);
    } catch { /* silent */ }
  }, []);

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.active) params.append('active', filters.active);
      if (filters.wholesalerId) params.append('wholesalerId', filters.wholesalerId);
      const res = await axios.get(`${API_URL}/admin/platform/discounts?${params}`, { headers: getHeaders() });
      let results = res.data.discounts || [];
      // Client-side search filter (business/wholesaler name or reason)
      if (filters.search) {
        const q = filters.search.toLowerCase();
        results = results.filter(d =>
          d.wholesalerId?.companyName?.toLowerCase().includes(q) ||
          d.businessId?.businessName?.toLowerCase().includes(q) ||
          d.reason?.toLowerCase().includes(q)
        );
      }
      setDiscounts(results);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/platform/discounts/stats`, { headers: getHeaders() });
      setStats(res.data.stats);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchWholesalers(); }, [fetchWholesalers]);
  useEffect(() => { fetchDiscounts(); }, [fetchDiscounts]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API_URL}/admin/platform/discounts/${id}/toggle`, {}, { headers: getHeaders() });
      fetchDiscounts(); fetchStats();
    } catch (err) { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (discountId) => {
    setConfirmModal({
      title: t('customerDiscounts.deleteTitle', 'Delete Discount'),
      message: t('customerDiscounts.confirmDelete', 'Delete this discount permanently?'),
      icon: 'danger',
      confirmLabel: t('customerDiscounts.deleteButton', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await axios.delete(`${API_URL}/admin/platform/discounts/${discountId}`, { headers: getHeaders() });
          fetchDiscounts(); fetchStats();
        } catch (err) { toast.error('Failed to delete'); }
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Percent className="w-6 h-6 text-emerald-500" /> {t('customerDiscounts.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('customerDiscounts.subtitle')}</p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">{t('customerDiscounts.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">{t('customerDiscounts.stats.active')}</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">{t('customerDiscounts.stats.inactive')}</p>
                <p className="text-2xl font-bold text-gray-500">{stats.inactive}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">{t('customerDiscounts.stats.avgPercentage')}</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.avgPercentage}%</p>
              </div>
            </div>
          )}

          {/* Filters — with wholesaler dropdown + search */}
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input type="text" value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Search by name, business, reason..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
            </div>
            {/* Wholesaler filter */}
            <select value={filters.wholesalerId}
              onChange={e => setFilters(f => ({ ...f, wholesalerId: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm bg-white min-w-[180px]">
              <option value="">{t('catalogDiscounts.filters.allWholesalers', 'All Wholesalers')}</option>
              {wholesalers.map(w => (
                <option key={w._id} value={w._id}>{w.companyName}</option>
              ))}
            </select>
            {/* Status filter */}
            {[
              { v: '', l: 'All' },
              { v: 'true', l: t('catalogDiscounts.filters.active', 'Active') },
              { v: 'false', l: t('catalogDiscounts.filters.inactive', 'Inactive') }
            ].map(f => (
              <button key={f.v} onClick={() => setFilters(prev => ({ ...prev, active: f.v }))}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  filters.active === f.v ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'
                }`}>
                {f.l}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border">
            {loading ? (
              <div className="p-8 flex justify-center"><LoadingSpinner /></div>
            ) : discounts.length === 0 ? (
              <div className="p-8 text-center text-gray-400">{t('customerDiscounts.noDiscounts')}</div>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b text-xs text-gray-400 uppercase font-medium">
                  <div className="col-span-2">{t('customerDiscounts.table.wholesaler')}</div>
                  <div className="col-span-2">{t('customerDiscounts.table.business')}</div>
                  <div className="col-span-1 text-center">{t('customerDiscounts.table.percentage')}</div>
                  <div className="col-span-2">{t('customerDiscounts.table.reason')}</div>
                  <div className="col-span-2 text-center">{t('customerDiscounts.table.minOrder')}</div>
                  <div className="col-span-1 text-center">{t('customerDiscounts.table.status')}</div>
                  <div className="col-span-2 text-center">{t('customerDiscounts.table.actions')}</div>
                </div>
                <div className="divide-y">
                  {discounts.map((d) => (
                    <div key={d._id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50">
                      <div className="col-span-2 text-sm font-medium text-gray-900 truncate">
                        {d.wholesalerId?.companyName || 'Unknown'}
                      </div>
                      <div className="col-span-2 text-sm text-gray-700 truncate">
                        {d.businessId?.businessName || 'Unknown'}
                      </div>
                      <div className="col-span-1 text-center">
                        <span className="inline-flex px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-sm font-bold">
                          {d.percentage}%
                        </span>
                      </div>
                      <div className="col-span-2 text-xs text-gray-500 truncate">{d.reason || '—'}</div>
                      <div className="col-span-2 text-center text-sm text-gray-600">
                        {d.minimumOrderAmount ? `€${d.minimumOrderAmount}` : '—'}
                      </div>
                      <div className="col-span-1 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          d.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {d.active ? t('catalogDiscounts.filters.active', 'Active') : t('catalogDiscounts.filters.inactive', 'Inactive')}
                        </span>
                      </div>
                      <div className="col-span-2 flex justify-center gap-1">
                        <button onClick={() => handleToggle(d._id)} title="Toggle active"
                          className="p-1.5 rounded hover:bg-gray-100">
                          {d.active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                        </button>
                        <button onClick={() => handleDelete(d._id)} title="Delete"
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminDiscounts;